import { useState, useEffect, useRef, useCallback } from 'react'
import { createFileRoute, useRouter, Link } from '@tanstack/react-router'
import { ArrowLeft, Share, ChevronDown, MoreHorizontal } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useProduct } from '@/hooks/api/useProducts'
import { useCurrentCollectionToggle } from '@/hooks/useCurrentCollectionToggle'
import { useUserSummary } from '@/hooks/api/useUsers'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useCurrentCollectionStore } from '@/stores/currentCollection'
import { useCollection } from '@/hooks/api/useCollections'
import { Button } from '@/components/shared/atoms/Button'
import { SaveButton } from '@/components/shared/atoms/SaveButton'
import { TryOnButton } from '@/components/shared/atoms/TryOnButton'
import { AddToCollectionPanel } from '@/components/collections/AddToCollectionPanel'
import ProductBanner from '@/components/products/page/mobile/ProductBanner'
import { TopBarOverlay } from '@/components/shared/molecules/TopBarOverlay'
import { ProductImage } from '@/components/products/ProductImage'
import { ProductOptionsBottomSheet } from '@/components/products/bottom-sheets/ProductOptionsBottomSheet'
import { AddProductToCollectionBottomSheet } from '@/components/products/bottom-sheets/AddProductToCollectionBottomSheet'
import { EditProductBottomSheet } from '@/components/products/bottom-sheets/EditProductBottomSheet'
import { CarouselItem } from '@/components/shared/Carousel'
import Carousel, { CarouselRef } from '@/components/shared/Carousel'
import { ProductCarouselImage } from '@/components/products/page/mobile/ProductCarouselImage'
import { ProductOverlay } from '@/components/products/ProductOverlay'
import { useBottomNav } from '@/contexts/BottomNavContext'
import { useClickOutside } from '@/hooks/useClickOutside'
import { ProductImageGalleryModal } from '@/components/products/ProductImageGalleryModal'
import { TryOnModal } from '@/components/products/try-on/TryOnModal'
import { useDisplayPrice } from '@/hooks/useDisplayPrice'
import { formatCurrency } from '@/utils/formatCurrency'
import { useProtectedAction } from '@/hooks/useProtectedAction'
import { useShare } from '@/hooks/useShare'
import { transformSmall } from '@wtf/shared'
import { usePageViewTracking } from '@/hooks/analytics/usePageViewTracking'
import { AnalyticsEvents } from '@/utils/analytics'

export const Route = createFileRoute('/products/$id')({
    component: RouteComponent,
})

const Skeleton = () => {
    return (
        <div className="mx-2 mt-2 animate-pulse space-y-2 px-2">
            <div className="min-h-120 w-full rounded-lg bg-gray-200" />
            <div className="min-h-20 w-full rounded-lg bg-gray-200" />
            {Array.from({ length: 5 }).map((_, index) => (
                <div
                    key={index}
                    className="grid w-full grid-cols-2 items-center gap-2 rounded-lg"
                >
                    <div className="col-span-1 h-60 rounded-2xl bg-gray-200" />
                    <div className="col-span-1 h-60 rounded-2xl bg-gray-200" />
                </div>
            ))}
        </div>
    )
}

function RouteComponent() {
    const { id } = Route.useParams()
    const router = useRouter()
    const { user } = useCurrentUser()
    const bottomNav = useBottomNav()
    const { share } = useShare()
    const { withAuth } = useProtectedAction()
    const posthog = usePostHog()

    const [isAddToCollectionOpen, setIsAddToCollectionOpen] = useState(false)
    const [showOptions, setShowOptions] = useState(false)
    const [showAddCollection, setShowAddCollection] = useState(false)
    const [showEditProduct, setShowEditProduct] = useState(false)
    const [showImageGallery, setShowImageGallery] = useState(false)
    const [showTryOnModal, setShowTryOnModal] = useState(false)
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)
    const carouselRef = useRef<CarouselRef>(null)
    const addToCollectionButtonRef = useRef<HTMLButtonElement>(null)
    const addToCollectionPanelRef = useRef<HTMLDivElement>(null)

    const {
        data: product,
        isLoading: isProductLoading,
        error: productError,
    } = useProduct(id, { refetchOnMount: true }) // Explicitly refetch for detail page to ensure latest data
    const { displayPrice, displayCurrency } = useDisplayPrice(
        product?.price || 0,
        product?.currency || 'AUD'
    )
    const { data: owner, isLoading: isOwnerLoading } = useUserSummary(
        product?.ownerId || undefined
    )
    const {
        handleToggle: toggleCurrentCollection,
        isProductInCurrentCollection,
    } = useCurrentCollectionToggle(product)

    // Track page views and time on page
    const { trackAction } = usePageViewTracking({
        viewEvent: AnalyticsEvents.PRODUCT_PAGE_VIEWED,
        exitEvent: AnalyticsEvents.PRODUCT_PAGE_EXITED,
        viewProperties: {
            product_id: product?.id,
            product_name: product?.name,
            brand: product?.brand,
            price: product?.price,
            currency: product?.currency,
        },
        exitProperties: {
            product_id: product?.id,
        },
        dependencies: [product?.id],
    })

    // Use Zustand subscription to ensure React re-renders when store updates
    const currentCollectionId = useCurrentCollectionStore(
        (state) => state.currentCollectionId
    )
    const { data: currentCollection } = useCollection(currentCollectionId || '')
    const { data: productCollection, isLoading: isProductCollectionLoading } =
        useCollection(product?.collectionId || undefined)

    // Handle click outside for AddToCollectionPanel
    useClickOutside(
        [addToCollectionButtonRef, addToCollectionPanelRef],
        () => setIsAddToCollectionOpen(false),
        { enabled: isAddToCollectionOpen }
    )

    const isOwner = !!(
        product &&
        user &&
        product.ownerId &&
        product.ownerId === user.uid
    )

    const handleBack = () => {
        router.history.back()
    }

    const handleShare = async () => {
        // Track share event
        posthog?.capture(AnalyticsEvents.PRODUCT_SHARED, {
            product_id: product?.id,
            product_name: product?.name,
            brand: product?.brand,
        })

        await share({
            title: product?.name || 'Check out this product',
            text: product?.brand
                ? `${product.name} by ${product.brand}`
                : undefined,
            url: window.location.href,
        })
    }

    const handleSaveClick = () => {
        if (!product) return
        toggleCurrentCollection()
    }

    // Mobile-specific handlers
    const handleImageHeightChange = useCallback(() => {
        // Trigger AutoHeight recalculation when image height changes
        if (carouselRef.current?.emblaApi) {
            // Multiple reInit attempts with increasing delays for robustness
            const reinitWithDelay = (delay: number) => {
                setTimeout(() => {
                    if (carouselRef.current?.emblaApi) {
                        carouselRef.current.emblaApi.reInit()
                    }
                }, delay)
            }

            // Immediate reInit
            carouselRef.current.emblaApi.reInit()

            // Additional reInits with delays to handle edge cases
            reinitWithDelay(50)
            reinitWithDelay(150)
            reinitWithDelay(300)
        }
    }, [])

    const handleEditClick = () => {
        setShowOptions(false)
        setShowEditProduct(true)
    }

    const handleImageClick = (index: number) => {
        setSelectedImageIndex(index)
        setShowImageGallery(true)
    }

    const handleTryOnClick = withAuth(() => {
        // Track try-on button click with time-to-action
        trackAction('try_on')
        posthog?.capture(AnalyticsEvents.PRODUCT_TRY_ON_CLICKED, {
            product_id: product?.id,
            product_name: product?.name,
            brand: product?.brand,
            source: 'product_page',
        })
        setShowTryOnModal(true)
    })

    const handleBuyNowClick = () => {
        // Track buy now click with time-to-action
        trackAction('buy_now')
        posthog?.capture(AnalyticsEvents.PRODUCT_BUY_NOW_CLICKED, {
            product_id: product?.id,
            product_name: product?.name,
            brand: product?.brand,
            price: product?.price,
            currency: product?.currency,
            purchase_url: product?.purchaseUrl,
        })
    }

    const handleOptionsClick = () => {
        posthog?.capture(AnalyticsEvents.PRODUCT_OPTIONS_OPENED, {
            product_id: product?.id,
        })
        setShowOptions(true)
    }

    // Handle window resize for carousel height recalculation
    useEffect(() => {
        let debounceTimeout: NodeJS.Timeout
        const debouncedResize = () => {
            clearTimeout(debounceTimeout)
            debounceTimeout = setTimeout(handleImageHeightChange, 150)
        }

        window.addEventListener('resize', debouncedResize)
        return () => {
            window.removeEventListener('resize', debouncedResize)
            clearTimeout(debounceTimeout)
        }
    }, [handleImageHeightChange])

    // Recalculate carousel height after product data loads (for direct navigation)
    useEffect(() => {
        if (product && !isProductLoading) {
            // Delay to ensure images start loading and DOM is ready
            const timeouts = [
                setTimeout(handleImageHeightChange, 100),
                setTimeout(handleImageHeightChange, 300),
                setTimeout(handleImageHeightChange, 600),
                setTimeout(handleImageHeightChange, 1000),
            ]

            return () => {
                timeouts.forEach((timeout) => clearTimeout(timeout))
            }
        }
    }, [product, isProductLoading, handleImageHeightChange])

    const images = product?.images || []

    if (isProductLoading) {
        return (
            <>
                {/* Mobile Loading */}
                <div className="block md:hidden">
                    <Skeleton />
                </div>
                {/* Desktop Loading */}
                <div className="hidden min-h-dvh items-center justify-center md:flex">
                    Loading...
                </div>
            </>
        )
    }

    if (productError || !product) {
        return (
            <div className="flex min-h-dvh items-center justify-center">
                Product not found
            </div>
        )
    }

    return (
        <>
            {/* Mobile Version */}
            <div
                className="relative flex min-h-dvh w-full flex-col overflow-y-auto bg-white md:hidden"
                style={{
                    paddingBottom: bottomNav.isBottomNavVisible
                        ? `${bottomNav.bottomNavHeight + 60}px`
                        : undefined,
                }}
            >
                <TopBarOverlay
                    onOptionsClick={handleOptionsClick}
                    onBackClick={handleBack}
                />
                {/* Product Image Carousel */}
                <div className="relative">
                    <Carousel className="px-2 pt-2" ref={carouselRef}>
                        {images.length > 0 ? images.map((image, index) => (
                            <CarouselItem key={image.id || index}>
                                <ProductCarouselImage
                                    image={image}
                                    brand={product.brand}
                                    onHeightChange={handleImageHeightChange}
                                />
                                {/* Product Brand & Price Overlay on top of Carousel*/}
                                <ProductOverlay
                                    product={product}
                                    variant="bottom"
                                />
                            </CarouselItem>
                        )) : (
                            <CarouselItem>
                                <div className="min-h-[18.438rem] flex items-center justify-center bg-gray-100 rounded-2xl mx-2">
                                    <p className="text-gray-500">No images available</p>
                                </div>
                            </CarouselItem>
                        )}
                    </Carousel>
                </div>

                {/* Product Info */}
                <ProductBanner
                    title={product.name}
                    ownerId={product.ownerId ?? null}
                    productId={product.id}
                    onSaveClick={() => setShowAddCollection(true)}
                    onTryOnClick={handleTryOnClick}
                />

                <div className="mx-auto mt-4 w-full max-w-md px-2 md:max-w-4xl">
                    <a
                        href={product.purchaseUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full"
                        onClick={handleBuyNowClick}
                    >
                        <Button className="flex w-full items-center justify-center gap-2 border-[1.5px] border-black py-3.5 text-sm font-medium">
                            Buy now
                        </Button>
                    </a>
                </div>
            </div>

            {/* Desktop Version */}
            <div className="">
                <div className="mx-auto hidden max-w-496 px-8 pb-4 md:block">
                    {/* Main content area */}
                    <div className="flex">
                        {/* Left Side - Product Details */}
                        <div className="top-calc-header sticky h-fit flex-4">
                            <div className="pr- pl-4">
                                {/* Back Arrow */}
                                <div className="flex h-16 items-center">
                                    <button
                                        onClick={handleBack}
                                        className="-ms-2 cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-100"
                                    >
                                        <ArrowLeft size={20} />
                                    </button>
                                </div>

                                {/* Brand Name */}
                                <h1 className="bg-blu line-clamp-3 flex-wrap pb-4 text-6xl leading-tight font-bold md:text-6xl md:leading-tight lg:text-7xl lg:leading-tight xl:text-8xl xl:leading-tight">
                                    {product.brand}
                                </h1>

                                {/* Price */}
                                <p className="mt-2 mb-4 text-3xl font-normal">
                                    {formatCurrency(
                                        displayPrice,
                                        displayCurrency,
                                        true
                                    )}
                                </p>

                                {/* Description */}
                                <div className="mt-10 mb-6">
                                    <h3 className="text-lg font-normal">
                                        DESCRIPTION
                                    </h3>
                                    <p className="line-clamp-4 leading-relaxed text-gray-700">
                                        {product.description}
                                    </p>
                                </div>

                                {/* User & Collection Info */}
                                <div className="mb-6 flex gap-2">
                                    <div className="flex flex-col pr-8">
                                        <h3 className="text-lg font-normal">
                                            USER
                                        </h3>
                                        {isOwnerLoading ? (
                                            <div className="flex items-center py-2 pr-2">
                                                <div className="mr-2 h-8 w-8 animate-pulse rounded-full bg-gray-200"></div>
                                                <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                                            </div>
                                        ) : owner ? (
                                            <Link
                                                to="/profiles/$id"
                                                params={{ id: owner.id }}
                                                className="inline-flex items-center rounded-lg py-2 pr-2 transition-colors hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                                            >
                                                <div className="mr-2 h-8 w-8 overflow-hidden rounded-full bg-gray-300">
                                                    {owner.avatarUrl ? (
                                                        <img
                                                            src={
                                                                owner.avatarUrl
                                                            }
                                                            alt={owner.username}
                                                            className="h-full w-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="h-full w-full bg-gray-400"></div>
                                                    )}
                                                </div>
                                                <span className="font-medium">
                                                    {owner.username}
                                                </span>
                                            </Link>
                                        ) : null}
                                    </div>
                                    <div className="flex flex-col">
                                        <h3 className="text-lg font-normal">
                                            COLLECTION
                                        </h3>
                                        {isProductCollectionLoading ? (
                                            <div className="flex items-center gap-3 py-2 pr-2">
                                                <div className="h-8 w-8 animate-pulse rounded-lg bg-gray-200"></div>
                                                <div className="h-4 w-32 animate-pulse rounded bg-gray-200"></div>
                                            </div>
                                        ) : productCollection ? (
                                            <Link
                                                to="/collections/$id"
                                                params={{
                                                    id: productCollection.id,
                                                }}
                                                className="inline-flex items-center gap-3 rounded-lg py-2 pr-2 transition-colors hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                                            >
                                                <div className="h-8 w-8 overflow-hidden rounded-lg bg-gray-300">
                                                    {productCollection.coverImageUrl ? (
                                                        <img
                                                            src={
                                                                transformSmall(productCollection.coverImageUrl) || productCollection.coverImageUrl
                                                            }
                                                            alt={
                                                                productCollection.name
                                                            }
                                                            className="h-full w-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="h-full w-full bg-gray-400"></div>
                                                    )}
                                                </div>
                                                <span className="font-medium">
                                                    {productCollection.name}
                                                </span>
                                            </Link>
                                        ) : null}
                                    </div>
                                </div>

                                {/* Buy Button */}
                                <div className="mt-20 flex gap-3">
                                    <Button
                                        variant="outline"
                                        className="w-48 rounded-xl"
                                        onClick={() => {
                                            handleBuyNowClick()
                                            window.open(
                                                product.purchaseUrl,
                                                '_blank'
                                            )
                                        }}
                                    >
                                        Buy now
                                    </Button>
                                    <TryOnButton
                                        variant="long"
                                        className="w-48 rounded-xl px-4 py-3"
                                        onClick={handleTryOnClick}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex-1"></div>
                        {/* Right Side - Product Images List */}
                        <div className="max-w-4xl flex-4">
                            {/* Control buttons row - sticky */}
                            <div className="top-calc-header sticky z-1 flex h-16 items-center justify-between px-2">
                                <div className="flex items-center">
                                    <button
                                        onClick={handleShare}
                                        className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-100"
                                    >
                                        <Share size={20} />
                                    </button>
                                    <button
                                        onClick={handleOptionsClick}
                                        className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-100"
                                    >
                                        <MoreHorizontal size={20} />
                                    </button>
                                </div>

                                <div className="flex items-center gap-2">
                                    <div className="relative">
                                        <button
                                            ref={addToCollectionButtonRef}
                                            onClick={() =>
                                                setIsAddToCollectionOpen(
                                                    !isAddToCollectionOpen
                                                )
                                            }
                                            className="glass-background flex cursor-pointer items-center gap-2 rounded-xl px-4 py-3 text-sm transition-colors hover:bg-gray-400"
                                        >
                                            {currentCollection?.name || ''}
                                            <ChevronDown size={16} />
                                        </button>

                                        <AddToCollectionPanel
                                            ref={addToCollectionPanelRef}
                                            productId={product.id}
                                            open={isAddToCollectionOpen}
                                            onClose={() =>
                                                setIsAddToCollectionOpen(false)
                                            }
                                            size="large"
                                        />
                                    </div>

                                    <SaveButton
                                        isSaved={isProductInCurrentCollection}
                                        onClick={handleSaveClick}
                                        size="lg"
                                    />
                                </div>
                            </div>

                            {/* Images container with padding */}
                            <div className="space-y-2">
                                {product.images?.map((image, index) => (
                                    <div
                                        key={image.id || index}
                                        className="w-full cursor-pointer"
                                        onClick={() => handleImageClick(index)}
                                    >
                                        <ProductImage
                                            image={image}
                                            alt={`${product.brand} - Image ${index + 1}`}
                                            className="aspect-auto h-auto w-full max-w-full rounded-xl transition-opacity hover:opacity-90"
                                            imgClassName='w-full'
                                            loading="lazy"
                                            sizes="(max-width: 1024px) 100vw, 50vw"
                                        />
                                    </div>
                                ))}

                                {(!product.images ||
                                    product.images.length === 0) && (
                                        <div className="flex h-64 items-center justify-center rounded-lg bg-gray-100">
                                            <p className="text-gray-500">
                                                No images available
                                            </p>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Sheets */}
            <section className="fixed">
                {product && (
                    <ProductOptionsBottomSheet
                        open={showOptions}
                        onClose={() => setShowOptions(false)}
                        isOwner={isOwner}
                        onEditClick={isOwner ? handleEditClick : undefined}
                        product={product}
                    />
                )}

                {product && (
                    <AddProductToCollectionBottomSheet
                        open={showAddCollection}
                        onClose={() => setShowAddCollection(false)}
                        productId={product.id}
                    />
                )}

                {isOwner && product && (
                    <EditProductBottomSheet
                        open={showEditProduct}
                        onClose={() => setShowEditProduct(false)}
                        productData={product}
                    />
                )}

                {product && product.images && product.images.length > 0 && (
                    <ProductImageGalleryModal
                        images={product.images}
                        initialImageIndex={selectedImageIndex}
                        open={showImageGallery}
                        onClose={() => setShowImageGallery(false)}
                    />
                )}

                {product && (
                    <TryOnModal
                        open={showTryOnModal}
                        onClose={() => setShowTryOnModal(false)}
                        productId={product.id}
                        productImages={product.images || []}
                    />
                )}
            </section>
        </>
    )
}
